import React, { useEffect, useState } from "react";

import { useApolloClient, useLazyQuery, useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import logo from "@/common/Layout/images/atlas-sq.png";
import { ACCOUNT_ROLES_QUERY } from "@/graphql/queries/getAccountRoles";
import { BRAND_CONFIGS_QUERY } from "@/graphql/queries/getBrandConfigs";
import { GET_CONFIG_QUERY } from "@/graphql/queries/getConfig";
import { GET_MERCHANT_APPS_SIMPLE_QUERY } from "@/graphql/queries/getMerchantApps";
import { SIMPLE_OUTLETS_QUERY } from "@/graphql/queries/getOutlets";
import useAnalytics from "@/hooks/useAnalytics";
import {
  findCurrentMerchantAccount,
  isAdmin,
  isSuperAdmin,
} from "@/utils/merchantAccount";

import FloorPlanProvider from "./common/FloorPlan/components/FloorPlanProvider";
import { SidebarProvider } from "./common/Layout/sidebarContext";
import { AccountProvider } from "./components/Account";
import { AppProvider } from "./components/App/appContext";
import { ConfigProvider } from "./components/Config/configContext";
import { AudioEnabledProvider } from "./components/EnableAudioModal";
import { OutletsProvider } from "./components/Outlets";
import constants from "./utils/constants";

CustomProviders.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  audioEnabled: PropTypes.bool,
  audioRef: PropTypes.object,
  chimeRef: PropTypes.object,
  sidebarVisible: PropTypes.bool,
  setSidebarVisible: PropTypes.func,
  sidebarFirstLoad: PropTypes.bool,
  setSidebarFirstLoad: PropTypes.func,
  setMerchantChanged: PropTypes.func,
  setFeatures: PropTypes.func,
};

export default function CustomProviders({
  children,
  audioEnabled,
  audioRef,
  chimeRef,
  sidebarVisible,
  setSidebarVisible,
  sidebarFirstLoad,
  setSidebarFirstLoad,
  setMerchantChanged,
  setFeatures,
}) {
  const { t } = useTranslation();

  const [merchantIdentifier] = useState(
    localStorage.getItem(constants.X_MERCHANT_ID),
  );
  const [merchantName, setMerchantName] = useState("");
  const [merchantLogo, setMerchantLogo] = useState(logo);
  const [selectedOutletLabel, setSelectedOutletLabel] = useState("");
  const [selectedOutletId, setSelectedOutletId] = useState(null);
  const [accountIsAdmin, setAccountIsAdmin] = useState(null);
  const [accountIsSuperAdmin, setAccountIsSuperAdmin] = useState(null);
  const [currentMerchantAccount, setCurrentMerchantAccount] = useState(null);

  const client = useApolloClient();
  client.onClearStore(() => {
    setAccountIsAdmin(false);
    setAccountIsSuperAdmin(false);
    setMerchantName("");
  });

  const {
    loading: configLoading,
    data: configQuery,
    refetch: configRefetch,
  } = useQuery(GET_CONFIG_QUERY, {
    context: { graph: "restaurants" },
  });
  const { loading: brandConfigsLoading, data: brandConfigsQuery } = useQuery(
    BRAND_CONFIGS_QUERY,
    {
      skip: !localStorage.getItem(constants.ACCESS_TOKEN),
      context: { graph: "restaurants" },
    },
  );

  const { identify } = useAnalytics();
  const [fetchAccount, { loading: accountLoading, data: accountData }] =
    useLazyQuery(ACCOUNT_ROLES_QUERY, {
      context: { graph: "restaurants" },
      fetchPolicy: "cache-and-network",
      onCompleted: (data) => {
        if (data?.account) updateAccountData(data.account);
        identify(data?.account?.id, {
          email: data?.account?.email,
          current_merchant_identifier: findCurrentMerchantAccount(data?.account)
            ?.merchant?.identifier,
        });
      },
    });

  const { loading: outletsLoading, data: outletsData } = useQuery(
    SIMPLE_OUTLETS_QUERY,
    {
      skip: !localStorage.getItem(constants.ACCESS_TOKEN),
      context: { graph: "restaurants" },
      onCompleted: (data) => {
        if (data?.outlets) {
          const { outlets } = data;
          const xOutletID = localStorage.getItem(constants.X_OUTLET_ID);

          let outletLabel = t("sidebar.outletSelector.noOutletSelected");

          if (xOutletID === "all") {
            outletLabel = t("sidebar.outletSelector.all");
          } else if (xOutletID) {
            const selectedOutlet = outlets.find(
              (outlet) => outlet.id === parseInt(xOutletID),
            );
            if (selectedOutlet) {
              outletLabel = selectedOutlet.label;
            }
          }
          setSelectedOutletId(xOutletID);
          setSelectedOutletLabel(outletLabel);
        }
      },
    },
  );

  useEffect(() => {
    const xOutletID = localStorage.getItem(constants.X_OUTLET_ID);
    if (outletsData?.outlets && accountIsAdmin && !xOutletID) {
      // if no outlet selected, select all by default
      setSelectedOutletId("all");
      setSelectedOutletLabel(t("sidebar.outletSelector.all"));
    }
  }, [outletsData, accountIsAdmin, t]);

  const { loading: merchantAppLoading, data: merchantAppQuery } = useQuery(
    GET_MERCHANT_APPS_SIMPLE_QUERY,
    {
      context: { graph: "restaurants" },
    },
  );

  function checkMerchantChanged() {
    if (
      merchantIdentifier &&
      localStorage.getItem(constants.X_MERCHANT_ID) &&
      merchantIdentifier !== localStorage.getItem(constants.X_MERCHANT_ID)
    ) {
      // delay for 2s
      setTimeout(() => {
        setMerchantChanged(true);
      }, 2000);
    } else {
      setMerchantChanged(false);
      // just in case it changed back before the 2s delay
      setTimeout(() => {
        setMerchantChanged(false);
      }, 2000);
    }
  }

  function updateAccountData(account) {
    const merchantAccount = findCurrentMerchantAccount(account);
    if (merchantAccount) {
      setMerchantName(merchantAccount.merchant.name);
      if (merchantAccount.merchant.logoUrl)
        setMerchantLogo(merchantAccount.merchant.logoUrl);

      setAccountIsAdmin(isAdmin(account));
      setAccountIsSuperAdmin(isSuperAdmin(account));
      setCurrentMerchantAccount(merchantAccount);
    }
  }

  useEffect(() => {
    if (
      localStorage.getItem(constants.ACCESS_TOKEN) &&
      location.pathname !== "/login"
    ) {
      fetchAccount();
    }

    window.addEventListener("storage", checkMerchantChanged);

    return () => {
      window.removeEventListener("storage", checkMerchantChanged);
    };
  }, []);

  useEffect(() => {
    // automatically pin logout when navigating out of POS
    if (
      !location.pathname.includes("point-of-sale") &&
      localStorage.getItem(constants.PIN_TOKEN)
    ) {
      localStorage.removeItem(constants.PIN_TOKEN);
      localStorage.removeItem(constants.PIN_LOGIN_NAME);
      configRefetch();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (configQuery?.config?.currency) {
      localStorage.setItem(
        constants.CONFIG.CURRENCY,
        configQuery?.config?.currency,
      );
    }
  }, [configQuery?.config?.currency]);

  useEffect(() => {
    if (configQuery?.config?.features) {
      setFeatures(configQuery?.config?.features);
    }
  }, [configQuery?.config?.features]);

  return (
    <AppProvider>
      <ConfigProvider
        value={{
          configLoading,
          configQuery,
          configRefetch,
          brandConfigsLoading,
          brandConfigsQuery,
          merchantAppLoading,
          merchantAppQuery,
        }}
      >
        <SidebarProvider
          value={{
            sidebarVisible,
            setSidebarVisible,
            sidebarFirstLoad,
            setSidebarFirstLoad,
          }}
        >
          <AccountProvider
            value={{
              accountLoading,
              accountData,
              merchantName,
              merchantLogo,
              accountIsAdmin,
              accountIsSuperAdmin,
              currentMerchantAccount,
              fetchAccount,
            }}
          >
            <OutletsProvider
              value={{
                outletsLoading,
                outletsData,
                selectedOutletId,
                setSelectedOutletId,
                selectedOutletLabel,
                setSelectedOutletLabel,
              }}
            >
              <AudioEnabledProvider
                value={{ audioEnabled, audioRef, chimeRef }}
              >
                <FloorPlanProvider>{children}</FloorPlanProvider>
              </AudioEnabledProvider>
            </OutletsProvider>
          </AccountProvider>
        </SidebarProvider>
      </ConfigProvider>
    </AppProvider>
  );
}
